import * as React from "react";
import { useForm } from "react-hook-form";
import { ulid } from "ulid";
import { toast } from "react-toastify";
import * as analytics from "@/services/analytics";
import { Button } from "@/components/ui/button";
import { Search, SearchIcon } from "lucide-react";
import TextareaAutosize from "react-textarea-autosize";

interface FormInputs {
  query: string;
}

interface Props {
  query?: string;
  reset: () => void;
  handleSearch: (searchId: string, query: string) => Promise<void>;
}

export const GridSidebarSearchInput: React.FC<Props> = ({ query, handleSearch, reset }) => {
  const methods = useForm<FormInputs>({ defaultValues: { query: query || "" } });

  const handleReset = () => {
    reset();
    methods.reset({ query: "" });
  };

  const onSearch = async (payload: FormInputs) => {
    if (!payload.query) {
      return;
    }
    reset();

    const searchId = ulid();
    try {
      await handleSearch(searchId, payload.query);
      analytics.snapshotSearch(payload.query);
    } catch (error) {
      toast.error("Error searching for snapshots");
      console.error("Error searching for snapshots:", error);
    }
  };

  return (
    <form onSubmit={methods.handleSubmit(onSearch)} className="mb-4 flex items-center justify-center">
      <div className="relative mr-2 flex flex-grow justify-center">
        <TextareaAutosize
          {...methods.register("query")}
          placeholder="Search across SnapShots"
          minRows={1}
          maxRows={4}
          className="w-full resize-none rounded-md border border-gray-400 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none"
        />
        <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      </div>
      <Button type="submit" variant="coral" onClick={methods.handleSubmit(onSearch)}>
        <Search />
      </Button>

      {query && (
        <Button type="submit" variant="second-white" onClick={handleReset} className="ml-2">
          Clear
        </Button>
      )}
    </form>
  );
};
